import React, { useRef, useEffect, useCallback } from 'react'

import { ParallaxSlideProvider } from '../../../../contexts/parallax-context'
import { TypeSectionTwoColumn } from '../../../../lib/types'
import { SectionContent } from '../../../../lib/types/section'
import { throttle } from '../../../../lib/utils/throttle'
import { ParallaxSlideScrollingColumn, ParallaxSlideFixedColumn } from './columns'

import styles from '../parallax.module.scss'

const scrollingSlideIdPrefix = 'scrollingSlide-'

const throttleDelay = 500

const isSlideInViewportThreshold = (topSlide: number) => {
  if (IS_CLIENT_SIDE) {
    return topSlide < window.innerHeight - 200 // Check if top of Parallax slide is at least 200px within viewport. 200px is an arbitary number that worked well across screen sizes when testing.
  }

  return false
}

interface ParallaxDesktopProps {
  containerInternalName: string
  content: TypeSectionTwoColumn[]
  activeSlideIndex: number
  setActiveSlideIndex: (index: number) => void
}

export const ParallaxDesktop: React.FC<ParallaxDesktopProps> = ({
  containerInternalName,
  content,
  activeSlideIndex,
  setActiveSlideIndex,
}) => {
  const fixedColumnContainerRef = useRef<HTMLDivElement>(null)

  const slideSectionInternalNames = content.map((section) => section.fields.internalName)
  const leftContent = content.map((section) => section.fields.leftColumnContent) as SectionContent[]
  const rightContent = content.map((section) => section.fields.rightColumnContent) as SectionContent[]

  const scrollingColumnSlideRefs = useRef<React.RefObject<HTMLDivElement>[]>([])
  scrollingColumnSlideRefs.current = rightContent.map(
    (_, idx) => scrollingColumnSlideRefs.current[idx] ?? React.createRef<HTMLDivElement>()
  )

  const setClosestActiveSlide = useCallback(() => {
    // There are two columns: one is fixed, one is free scrolling.
    // Calculate which scrolling item's top is closest to the fixed column top.
    // Determines which slide is active.
    const topFixedColumnContainer = fixedColumnContainerRef.current?.getBoundingClientRect().top

    let minDistanceBetweenTops = Number.MAX_SAFE_INTEGER
    let nextActiveSlideIndex = -1

    scrollingColumnSlideRefs.current.forEach((slideRef, idx) => {
      const topScrollingColumnSlide = slideRef.current?.getBoundingClientRect().top

      if (topFixedColumnContainer && topScrollingColumnSlide && isSlideInViewportThreshold(topScrollingColumnSlide)) {
        const distanceBetweenTops = Math.abs(topFixedColumnContainer - topScrollingColumnSlide)

        if (distanceBetweenTops < minDistanceBetweenTops) {
          minDistanceBetweenTops = distanceBetweenTops
          nextActiveSlideIndex = idx
        }
      }
    })

    if (nextActiveSlideIndex > -1) {
      setActiveSlideIndex(nextActiveSlideIndex)
    }
  }, [setActiveSlideIndex])

  useEffect(() => {
    setClosestActiveSlide()

    const throttleHandleScroll = throttle(setClosestActiveSlide, throttleDelay)

    window.addEventListener('scroll', throttleHandleScroll)

    return () => {
      window.removeEventListener('scroll', throttleHandleScroll)
    }
  }, [setClosestActiveSlide])

  return (
    <div className={styles.parallaxDesktopContainer}>
      <div className={styles.scrollingColumnContainer}>
        {leftContent.map((content, idx) => {
          return (
            <ParallaxSlideScrollingColumn
              key={idx}
              ref={scrollingColumnSlideRefs.current[idx]}
              id={`${scrollingSlideIdPrefix}${idx}`}
              content={content}
              slideSectionInternalName={slideSectionInternalNames[idx]}
            />
          )
        })}
      </div>
      <div ref={fixedColumnContainerRef} className={styles.fixedColumnContainer}>
        {rightContent.map((content, idx) => {
          return (
            <ParallaxSlideProvider key={idx} value={{ containerName: containerInternalName, index: idx }}>
              <ParallaxSlideFixedColumn
                scrollingSlideId={`${scrollingSlideIdPrefix}${idx}`}
                content={content}
                isActive={activeSlideIndex === idx}
              />
            </ParallaxSlideProvider>
          )
        })}
      </div>
    </div>
  )
}
